import { useEffect, useState } from "react";
import SubMenu from "./SubMenu";
import { cache } from "./components/custom/Cache";

import { listenFor } from "./utils/messaging/messaging";

import { Box } from "@mui/material";
import {
    AutoStories as AmpStoriesIcon,
    Apps as AppsIcon,
    Assessment as AssessmentIcon,
    AssignmentInd as AssignmentIndIcon,
    Book as BookIcon,
    Business as BusinessIcon,
    Call as CallIcon,
    CompareArrows as CompareArrowsIcon,
    Error as ErrorIcon,
    Group as GroupIcon,
    LibraryBooks as LibraryBooksIcon,
    ListAlt as ListAltIcon,
    LocalHospital as LocalHospitalIcon,
    PermDataSetting as PermDataSettingIcon,
    PermDeviceInformation as PermDeviceInformationIcon,
    Person as PersonIcon,
    PhonelinkLock as PhonelinkLockIcon,
    PhonelinkSetup as PhonelinkSetupIcon,
    Preview as PreviewIcon,
    Satellite as SatelliteIcon,
    Security as SecurityIcon,
    Storage as StorageIcon,
    VerticalSplit as VerticalSplitIcon,
    Web as WebIcon,
} from "@mui/icons-material";
import { AppBar, DashboardMenuItem, Layout, MenuItemLink, usePermissions } from "react-admin";

const ON_PREM_PERMISSIONS = ["on_prem_files.read", "on_prem_files.read_all"];

import apps from "./apps.json";
import { EventType } from "./utils/messaging/event-type";

const classes = {
    root: {
        "& .MuiListItemIcon-root": {
            minWidth: "40px",
        },
        "& .MuiListItemText-primary": {
            fontSize: "1rem",
            color: "rgba(0, 0, 0, 0.54)",
        },
        "& .MuiButtonBase-root": {
            width: "250px",
            whiteSpace: "initial",
        },
    },
    selectedLink: {
        color: "#000",
        fontSize: "1rem",
    },
};

const menuCategories = [
    {
        alias: "apps",
        title: "Applications",
        icon: <AppsIcon />,
        subCategories: [
            { name: "portal/sagTemp", link: "/portal/sag-temp", text: "Sag Temp", icon: <SatelliteIcon /> },
        ],
    },
    {
        alias: "portalAdmin",
        title: "Portal Admin",
        icon: <WebIcon />,
        subCategories: [
            { name: "portal/sites", link: "/portal/sites", text: "Site Groups", icon: <BookIcon /> },
            {
                name: "portal/siteGroups",
                link: "/portal/siteGroups",
                text: "Site Group Lists",
                icon: <LibraryBooksIcon />,
            },
            {
                name: "portal/lineRoleGroups",
                link: "/portal/lineRoleGroups",
                text: "Line Role Groups",
                icon: <ListAltIcon />,
            },
            { name: "portal/users", link: "/portal/users", text: "Users", icon: <PersonIcon /> },
            {
                name: "portal/userAnalytics",
                link: "/portal/user-analytics",
                text: "User Analytics",
                icon: <AssignmentIndIcon />,
            },
            { name: "portal/roles", link: "/portal/roles", text: "Roles", icon: <SecurityIcon /> },
            { name: "portal/roleGroups", link: "/portal/roleGroups", text: "Role Groups", icon: <GroupIcon /> },
            {
                name: "portal/clientCharts",
                link: "/portal/clientCharts",
                text: "Client Charts",
                icon: <AssessmentIcon />,
            },
            {
                name: "portal/chartSubcharts",
                link: "/portal/chartSubcharts",
                text: "Chart Subcharts",
                icon: <AssessmentIcon />,
            },
            { name: "portal/chartSeries", link: "/portal/chartSeries", text: "Chart Series", icon: <ListAltIcon /> },
            { name: "portal/alertRules", link: "/portal/alertRules", text: "Alert Rules", icon: <ErrorIcon /> },
            { name: "portal/clients", link: "/portal/clients", text: "Clients", icon: <BusinessIcon /> },
            { name: "portal/onPremFiles", link: "/portal/onPremFiles", text: "On-Prem Files", icon: <StorageIcon /> },
            { name: "portal/clientMfa", link: "/portal/clientMfa", text: "Client MFA", icon: <PhonelinkLockIcon /> },
            { name: "portal/auditTrail", link: "/portal/auditTrail", text: "Audit Trail", icon: <ListAltIcon /> },
            { name: "masterdata/assets/v3", link: "/masterdata/assets/v3", text: "v3 status", icon: <ListAltIcon /> },
        ],
    },
    {
        alias: "lineRatings",
        title: "Line Ratings",
        icon: <PhonelinkSetupIcon />,
        subCategories: [
            {
                name: "portal/emergency-rating-config",
                link: "/portal/emergency-rating-config",
                text: "Emergency Rating Config",
                icon: <PermDataSettingIcon />,
            },
            {
                name: "portal/emergency-rating-config-section",
                link: "/portal/emergency-rating-config-section",
                text: "ER Config Sections",
                icon: <PermDataSettingIcon />,
            },
            {
                name: "/portal/confidence-interval-configuration",
                link: "/portal/confidence-interval-configuration",
                text: "Confidence Intervals",
                icon: <PermDataSettingIcon />,
            },
        ],
    },
    {
        alias: "telemetry",
        title: "Device Config",
        icon: <PhonelinkSetupIcon />,
        subCategories: [
            {
                name: "telemetry/edgeConfig/v3",
                link: "/telemetry/edgeConfig/v3",
                text: "V3 Config",
                icon: <PermDataSettingIcon />,
            },
        ],
    },
    {
        alias: "deviceHealth",
        title: "Device Health",
        icon: <PermDeviceInformationIcon />,
        subCategories: [
            {
                name: "telemetry/v3DeviceHealth",
                link: "/telemetry/v3-device-health",
                text: "V3 Device Health",
                icon: <LocalHospitalIcon />,
            },
            {
                name: "telemetry/deviceHealth",
                link: "/telemetry/device-health",
                text: "Device Health",
                icon: <LocalHospitalIcon />,
            },
            {
                name: "telemetry/deviceComms",
                link: "/telemetry/device-comms",
                text: "Device Comms",
                icon: <CallIcon />,
            },
        ],
    },
    {
        alias: "calibrationPlatform",
        title: "Calibration Platform",
        icon: <StorageIcon />,
        subCategories: [
            {
                name: "calibration/clientAsset",
                link: "/calibration/clientAsset",
                text: "ROOT Ingress",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/plscaddKmz",
                link: "/calibration/plscaddKmz",
                text: "PLSCADD KMZ Ingress",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/pitUpload",
                link: "/calibration/pitUpload",
                text: "PIT File Ingress",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/pitPipeline",
                link: "/calibration/pitPipeline",
                text: "Historical DLR Pipeline",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/Datasets",
                link: "/calibration/Datasets",
                text: "Latest Dataset Selector",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/masterdata",
                link: "/calibration/masterdata",
                text: "Masterdata Upload",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/palmCoefficients",
                link: "/calibration/palmCoefficients",
                text: "Palm",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/maple",
                link: "/calibration/maple",
                text: "Maple",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/palmCatenaryViewer",
                link: "/calibration/palmCatenaryViewer",
                text: "Palm Catenary Viewer",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/sensorCounts",
                link: "/calibration/sensorCounts",
                text: "Sensor Counts",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/aspen",
                link: "/calibration/aspen",
                text: "Sensor Placement",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/deviceClientAssetLinking",
                link: "/calibration/deviceClientAssetLinking",
                text: "Device Install Linking",
                icon: <AmpStoriesIcon />,
            },
            {
                name: "calibration/progressViewer",
                link: "/calibration/progressViewer",
                text: "Progress Viewer",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "calibration/referenceRatingUpload",
                link: "/calibration/referenceRatingUpload",
                text: "Reference Rating Upload",
                icon: <VerticalSplitIcon />,
            },
            {
                name: "portal/siteGroupCheatSheet",
                link: "/portal/sitesSummary",
                text: "Site Group Cheat Sheet",
                icon: <ListAltIcon />,
            },
        ],
    },
    {
        alias: "10x_Command_Control",
        title: "10x Development",
        icon: <PreviewIcon />,
        subCategories: [
            {
                name: "10x/viewport",
                link: "/10x/viewport",
                text: "10x Viewport",
                icon: <PreviewIcon />,
            },
        ],
    },
];

for (const app of apps) {
    if (app.menuAlias) {
        let menuItem = menuCategories.find((category) => category.alias == app.menuAlias);
        if (menuItem?.subCategories) {
            menuItem.subCategories.push(...apps);
        }
    } else if (app.id === "masterdata") {
        // Render Masterdata as a direct sidebar link using env/config
        menuCategories.push({
            alias: app.id,
            title: app.label,
            icon: <ListAltIcon />,
            subCategories: [
                {
                    name: app.id,
                    link:
                        window.env?.masterdataUiUrl ||
                        config.masterdataUiUrl ||
                        "https://masterdata-ui-prod.linevisioninc.com/",
                    text: app.label,
                    icon: <ListAltIcon />,
                },
            ],
        });
    } else {
        menuCategories.push({
            alias: app.id,
            icon: <AppsIcon />,
            title: app.label,
            ...app,
        });
    }
}

const initialState = {
    apps: true,
    portalAdmin: true,
    deviceConfig: true,
    deviceHealth: true,
    masterData: true,
};

const createHiddenIframe = (app) => {
    // Create and set up the iframe
    const iframe = document.createElement("iframe");

    iframe.src = `${window.env[app.env_id]}`;
    iframe.style.width = "0px";
    iframe.style.height = "0px";
    iframe.style.display = "none";
    iframe.style.border = "none";

    // Though 'key' is typically a React prop, in this context it just serves as a custom attribute for identification
    iframe.setAttribute("key", app.env_id);

    // Append iframe to the body (or another appropriate container)
    document.body.appendChild(iframe);

    // Clean up the iframe when the component unmounts
    return () => {
        document.body.removeChild(iframe);
    };
};

function deCamelCase(str) {
    // Add a space before each uppercase letter that's not at the start or after another uppercase letter
    // Split string into words and capitalize the first letter of each
    return str
        .replace(/(?<=[a-z])([A-Z])/g, " $1")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}

const CustomMenu = ({ onMenuClick }) => {
    const [state, setState] = useState(cache.get("sidebarMenu")?.[0] ?? initialState);
    const [menuItems, setMenuItems] = useState(menuCategories);
    const { permissions } = usePermissions();
    const hasOnPremAccess = permissions?.some((p) => ON_PREM_PERMISSIONS.includes(p));

    const appendSubItems = (appId, items) => {
        const config = menuItems.find((item) => item.alias === appId);

        if (!items) {
            console.warn("items not defined");
            return;
        }

        if (config) {
            config.subCategories = items.map((item) => ({
                name: item,
                link: `portal/app/${appId}/${item}`,
                text: deCamelCase(item),
                icon: <CompareArrowsIcon />,
            }));
        } else {
            console.error(`Could not find core menu item for ${appId}`);
        }

        setMenuItems([...menuItems]);
    };

    useEffect(() => {
        apps.forEach((app) => {
            createHiddenIframe(app);
        });
    }, []);

    useEffect(() => {
        return listenFor(EventType.REGISTER_MENU, (event) => {
            appendSubItems(event.appId, JSON.parse(event.menu));
        });
    }, []);

    const handleToggle = (categoryName) => {
        const clonedState = { ...state };

        clonedState[categoryName] = !state[categoryName];

        cache.set("sidebarMenu", [clonedState], 60);

        setState(clonedState);
    };

    return (
        <Box
            component="div"
            sx={{
                ...classes.root,
                paddingBottom: "100px",
            }}
        >
            <DashboardMenuItem onClick={onMenuClick} />

            {menuItems.map((category, index) => {
                const { alias, title, icon, subCategories } = category;
                // If only one subCategory and it's Masterdata, render as direct link
                if (alias === "masterdata" && subCategories && subCategories.length === 1) {
                    const item = subCategories[0];
                    return (
                        <MenuItemLink
                            key={`${item.name}-${index}`}
                            to={item.link}
                            primaryText={title}
                            leftIcon={icon}
                            onClick={onMenuClick}
                            target="_blank"
                            rel="noopener noreferrer"
                        />
                    );
                }
                // Otherwise, render as dropdown
                return (
                    <SubMenu
                        key={`${alias}-${index}`}
                        sx={state[alias] ? classes.selectedLink : ""}
                        handleToggle={() => handleToggle(alias)}
                        isOpen={state[alias]}
                        sidebarIsOpen={true}
                        name={title}
                        icon={icon}
                        dense={false}
                    >
                        {subCategories?.filter((item) =>
                            item.name !== "portal/onPremFiles" || hasOnPremAccess
                        ).map((item) => (
                            <MenuItemLink
                                key={`${item.name}-${index}`}
                                to={item.link || item.name}
                                primaryText={item.text}
                                leftIcon={item.icon}
                                onClick={onMenuClick}
                            />
                        ))}
                    </SubMenu>
                );
            })}
        </Box>
    );
};

import PropTypes from "prop-types";

const CustomLayout = ({ children, ...props }) => (
    <Layout {...props} menu={CustomMenu} appBar={AppBar} sx={{ paddingTop: "10px" }}>
        {children}
    </Layout>
);

CustomLayout.propTypes = {
    children: PropTypes.node.isRequired,
};

export default CustomLayout;
